import type { ElementRef, SyntheticEvent } from 'react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import type { ProjectType } from '@atlassian/jira-common-constants/src/project-types';
import type FetchError from '@atlassian/jira-fetch/src/utils/errors.tsx';
import type { MessageDescriptorV2 as MessageDescriptor } from '@atlassian/jira-intl/src/v2/types.tsx';

export type Error = FetchError | null;

export type AppSummary = {
	name: string;
	displayName: string;
	url: string;
};

export type CheckboxData = {
	key: string;
	name: string;
	description?: string;
	isChecked?: boolean;
	category?: string;
	requiredPermissions?: string[];
	isIndeterminate?: boolean;
	bannerMsg?: MessageDescriptor;
	logo?: string;
	url?: string;
};
export type CheckboxProps = {
	isParent?: boolean;
	isDisabled?: boolean;
	hasChild?: boolean;
	data: CheckboxData;
	childrenVisible?: boolean;
	onToggleChildren?: () => void;
	onChange?: (value: string, isChecked: boolean) => void;
};

export type CheckboxGroupData = CheckboxData & {
	child: CheckboxData[];
};

export type PermissionType = 'APP_PERMISSIONS' | 'DEFAULT_PERMISSIONS';

export type OnClose = (e: SyntheticEvent, analyticsEvent: UIAnalyticsEvent) => void;

export type OnOpen = () => void;

export type RoleItem = {
	id: string;
	name: string;
	translatedName?: string;
	description: string;
	roleOrigin: string;
	allowedOperations: string[];
	permissions: string[];
};

export type JiraRoleItem = {
	roleId: string;
	name: string;
	translatedName?: string;
	description: string;
	roleOrigin: string;
	allowedOperations: string[];
	permissions: string[];
};

export type Permission = {
	key: string;
	name: string;
	description: string;
	isChecked?: boolean;
	bannerMsg?: MessageDescriptor;
	category?: string;
	requiredPermissions?: string[];
	app?: AppSummary;
};

export type PermissionResponse = {
	permissions: Permission[];
	categories: Permission[];
};

export type Container = {
	id: string;
	type: string;
};

export type VersionToken = {
	counter: number;
	id: string;
};

export type ManageRolesContext = {
	baseUrl: string;
	projectId: number;
	cloudId: string;
	projectKey: string;
	projectName: string;
	projectType: ProjectType;
	isAdmin: boolean;
};

export type CustomRolesProps = {
	isOpen: boolean;
	onStopPerfMetric?: () => void;
	onClose: (
		e: SyntheticEvent,
		analyticsEvent: UIAnalyticsEvent,
		isOperationPerformed: boolean,
	) => void;
};

export type MessagesKeyTypes = 'genericErrorTitle' | 'genericErrorMsg';

export type RoleNameFieldWrapperRef = {
	current: null | ElementRef<'div'>;
};

export type EditRoleModalMessageKeys =
	| 'saveCustomRole'
	| 'closeCustomRole'
	| 'discardCustomRole'
	| 'createCustomRole'
	| 'updateCustomRole'
	| 'editRole'
	| 'createRole'
	| 'duplicateRole'
	| 'genericSuccessTitle'
	| 'roleCreationSuccessMsg'
	| 'roleUpdateSuccessMsg'
	| 'copyRoleName'
	| 'roleSaveSuccessMsg';

export type OperationEnum = string;
export const toOperation = (value: string): OperationEnum => value;
